import { useEffect, useMemo, useState } from 'react';
import { CircleAlert, Star } from 'lucide-react';
import { Modal, toast } from '../../../ui';
import { canReview, createReview } from '../../../services/reviewService';
import { getErrorMessage } from '../../../utils/apiError';
import { ProductThumb } from './OrderItemsList';

const MAX_COMMENT = 2000; // ReviewService.MaxCommentLength on the server

const STAR_WORDS = ['', 'Poor', 'Fair', 'Good', 'Very good', 'Excellent'];

/** Star rating as a radio group: arrow keys move between stars, each has a spoken label. */
const StarInput = ({ value, onChange, describedBy }) => {
  const [hover, setHover] = useState(0);
  const shown = hover || value;

  return (
    <fieldset className="pk-stars" aria-describedby={describedBy}>
      <legend className="pk-label">Your rating</legend>
      <div className="pk-stars__row" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map(n => (
          <label key={n} className={`pk-stars__star${n <= shown ? ' is-on' : ''}`} onMouseEnter={() => setHover(n)}>
            <input
              type="radio"
              name="review-rating"
              value={n}
              checked={value === n}
              onChange={() => onChange(n)}
              className="pk-account__sr"
            />
            <Star aria-hidden="true" />
            <span className="pk-account__sr">
              {n} {n === 1 ? 'star' : 'stars'}, {STAR_WORDS.at(n)}
            </span>
          </label>
        ))}
        <span className="pk-stars__word" aria-hidden="true">
          {shown ? STAR_WORDS.at(shown) : ''}
        </span>
      </div>
    </fieldset>
  );
};

const ReviewDialog = ({ item, onClose, onSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async e => {
    e.preventDefault();
    if (submitting) return;
    if (!rating) {
      setError('Choose a rating from 1 to 5 stars.');
      return;
    }
    if (comment.trim().length < 3) {
      setError('Write a few words about the project.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await createReview({ productId: item.productId, rating, comment: comment.trim() });
      toast.success('Thanks, your review was submitted.');
      onSubmitted(item.productId);
    } catch (err) {
      setError(getErrorMessage(err, "We couldn't submit your review. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={`Review ${item.productName}`} footer={null} width={520}>
      <form className="pk-form pk-review-form" onSubmit={submit} noValidate>
        <StarInput value={rating} onChange={setRating} />
        <div className="pk-field">
          <label className="pk-label" htmlFor="review-comment">
            Your review
          </label>
          <textarea
            id="review-comment"
            className="pk-textarea"
            value={comment}
            maxLength={MAX_COMMENT}
            onChange={e => setComment(e.target.value)}
            aria-describedby="review-comment-hint"
            placeholder="What did you build with it? How were the code and documentation?"
          />
          <p className="pk-hint" id="review-comment-hint">
            {comment.length}/{MAX_COMMENT} characters
          </p>
        </div>
        {error && (
          <div className="pk-alert" data-tone="danger" role="alert">
            <CircleAlert aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}
        <div className="pk-settings__actions">
          <button type="submit" className="pk-btn pk-btn--primary pk-btn--sm" aria-busy={submitting || undefined}>
            Submit review
          </button>
          <button type="button" className="pk-btn pk-btn--secondary pk-btn--sm" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

/**
 * One row per purchased project. "Write a review" shows only when the server allows it
 * (GET Review/can-review/{productId}); otherwise its reason is shown.
 */
const OrderReviews = ({ items }) => {
  // Unique products in the order
  const products = useMemo(() => {
    const seen = new Set();
    return (items || []).filter(item => {
      if (!item?.productId || seen.has(item.productId)) return false;
      seen.add(item.productId);
      return true;
    });
  }, [items]);
  const productKey = products.map(p => p.productId).join(',');

  const [eligibility, setEligibility] = useState({});
  const [reviewing, setReviewing] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const ids = productKey.split(',').filter(Boolean);
    setEligibility({});
    ids.forEach(id => {
      canReview(id).then(result => {
        if (cancelled) return;
        setEligibility(prev => ({
          ...prev,
          [id]: { canReview: Boolean(result?.canReview), reason: result?.reason || null },
        }));
      });
    });
    return () => {
      cancelled = true;
    };
  }, [productKey]);

  if (!products.length) return null;

  const onSubmitted = productId => {
    setReviewing(null);
    setEligibility(prev => ({
      ...prev,
      [productId]: { canReview: false, reason: 'You have already reviewed this product.' },
    }));
  };

  return (
    <section className="pk-card" aria-labelledby="order-reviews-title">
      <div className="pk-card__head">
        <h2 className="pk-card__title" id="order-reviews-title">
          Reviews
        </h2>
      </div>
      <ul className="pk-review-list">
        {products.map(item => {
          const status = eligibility[item.productId];
          return (
            <li key={item.productId} className="pk-review-row">
              <ProductThumb src={item.productImage} size="sm" />
              <span className="pk-review-row__name">{item.productName}</span>
              <div className="pk-review-row__action" aria-live="polite">
                {!status ? (
                  <span className="pk-skel pk-review-row__skel">
                    <span className="pk-account__sr">Checking whether you can review this project</span>
                  </span>
                ) : status.canReview ? (
                  <button
                    type="button"
                    className="pk-btn pk-btn--secondary pk-btn--xs"
                    onClick={() => setReviewing(item)}
                    aria-label={`Write a review of ${item.productName}`}
                  >
                    Write a review
                  </button>
                ) : (
                  <span className="pk-review-row__reason">
                    {status.reason || "Reviews aren't available for this project right now."}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {reviewing && (
        <ReviewDialog item={reviewing} onClose={() => setReviewing(null)} onSubmitted={onSubmitted} />
      )}
    </section>
  );
};

export default OrderReviews;
